import { useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Grid2,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment";
import {
	useCostCenters,
	useMillSizes,
	useRollApplications,
	useRollMaterials,
	useSuppliers,
} from "../hooks/useRollshopOperations";
import type { RollListItem } from "../models/RollshopOperations";
import type { RollUpsertPayload } from "../repositories/RollshopOperationsRepository";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

const DATE_FORMAT = "YYYY-MM-DD";
const MIN_DATE = moment("1900-01-01", DATE_FORMAT);

// Only the date part is read, with no timezone conversion, the same way the
// grid renders it (formatProxyDate).
const toDateValue = (raw: string | null | undefined): Moment | null => {
	if (!raw) return null;
	return moment(String(raw).slice(0, 10), DATE_FORMAT);
};

const toDateString = (value: Moment | null): string | null =>
	value?.isValid() ? value.format(DATE_FORMAT) : null;

type DateField =
	| "OrderTimestamp"
	| "ReceiveTimestamp"
	| "InvoiceTimestamp"
	| "Returned";

const EMPTY_PATCH: Omit<RollUpsertPayload, "Id" | "Name"> = {
	Description: null,
	RollApplicationId: null,
	BlankDiameter: null,
	Diameter: null,
	Hardness: null,
	RollMaterialId: null,
	MillSizeId: null,
	Cost: null,
	CostCenterId: null,
	OrderTimestamp: null,
	ReceiveTimestamp: null,
	InvoiceTimestamp: null,
	PurchaseOrderItemId: null,
	SupplierId: null,
	SupplierInvoiceNumber: null,
	Returned: null,
};

const parseFloatOrNull = (v: string): number | null => {
	const t = v.trim();
	if (!t) return null;
	const n = Number(t);
	return Number.isFinite(n) ? n : null;
};

const parseIntOrNull = (v: string): number | null => {
	const t = v.trim();
	if (!t) return null;
	const n = Number.parseInt(t, 10);
	return Number.isFinite(n) ? n : null;
};

interface Props {
	roll: RollListItem | null;
	isSaving: boolean;
	disabled?: boolean;
	disabledReason?: string;
	onSave: (payload: RollUpsertPayload) => void;
	onClose: () => void;
}

// Detail panel with the autosave-on-blur pattern + the project-wide
// master-detail form standard (see feedback_master_detail_form_standard).
//
// - Existing roll (Id > 0): no SAVE button. Each TextField and date saves on
//   blur (a date also when picked from the calendar), each Select on change,
//   only if the value actually changed. Errors short-circuit the save (e.g.
//   empty Name). Only the edited field is sent: the procedure keeps every
//   NULL column and preserves values in fields the user did not edit.
// - New roll (Id null/0): no autosave. CREATE button is shown until the
//   record gets an Id. Then it switches to autosave mode automatically.
export const RollDetailPanel = ({
	roll,
	isSaving,
	disabled = false,
	disabledReason,
	onSave,
	onClose,
}: Props) => {
	const millSizesQuery = useMillSizes();
	const applicationsQuery = useRollApplications();
	const materialsQuery = useRollMaterials();
	const costCentersQuery = useCostCenters();
	const suppliersQuery = useSuppliers();

	const isNew = roll != null && (roll.Id == null || roll.Id <= 0);
	const isReadOnly = disabled || isSaving;

	// Local form state, initialised from the selected roll and reset whenever
	// the selected row id changes.
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [millSizeId, setMillSizeId] = useState<number | null>(null);
	const [applicationId, setApplicationId] = useState<number | null>(null);
	const [materialId, setMaterialId] = useState<number | null>(null);
	const [costCenterId, setCostCenterId] = useState<number | null>(null);
	const [supplierId, setSupplierId] = useState<number | null>(null);
	const [diameter, setDiameter] = useState<string>("");
	const [blankDiameter, setBlankDiameter] = useState<string>("");
	const [hardness, setHardness] = useState<string>("");
	const [cost, setCost] = useState<string>("");
	const [scrapWeight, setScrapWeight] = useState<string>("");
	const [dates, setDates] = useState<Record<DateField, Moment | null>>({
		OrderTimestamp: null,
		ReceiveTimestamp: null,
		InvoiceTimestamp: null,
		Returned: null,
	});
	const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState("");
	// A calendar pick commits and then the field blurs with the same value,
	// before the refetch brings the new stored date.
	const lastSentDate = useRef<Partial<Record<DateField, string>>>({});

	// Repopulate ONLY when a different roll is selected (Id changes). A
	// background refetch that returns the same Id with refreshed data
	// (post-autosave) must NOT clobber the values the user is typing in.
	// biome-ignore lint/correctness/useExhaustiveDependencies: deliberately
	// keyed on `roll?.Id` to avoid resetting form on refetch.
	useEffect(() => {
		if (!roll) return;
		setName(roll.Name ?? "");
		setDescription(roll.Description ?? "");
		setMillSizeId(roll.MillSizeId);
		setApplicationId(roll.RollApplicationId);
		setMaterialId(roll.RollMaterialId);
		setCostCenterId(roll.CostCenterId);
		setSupplierId(roll.SupplierId);
		setDiameter(roll.Diameter == null ? "" : String(roll.Diameter));
		setBlankDiameter(
			roll.BlankDiameter == null ? "" : String(roll.BlankDiameter),
		);
		setHardness(roll.Hardness == null ? "" : String(roll.Hardness));
		setCost(roll.Cost == null ? "" : String(roll.Cost));
		setScrapWeight(roll.ScrapWeight == null ? "" : String(roll.ScrapWeight));
		setDates({
			OrderTimestamp: toDateValue(roll.OrderTimestamp),
			ReceiveTimestamp: toDateValue(roll.ReceiveTimestamp),
			InvoiceTimestamp: toDateValue(roll.InvoiceTimestamp),
			Returned: toDateValue(roll.Returned),
		});
		setSupplierInvoiceNumber(roll.SupplierInvoiceNumber ?? "");
		lastSentDate.current = {};
	}, [roll?.Id]);

	const nameError = useMemo(() => {
		if (!name?.trim()) return "Name is required";
		if (name.trim().length > 12) return "Max 12 characters";
		return "";
	}, [name]);

	const canCreate =
		!isReadOnly && !nameError && roll != null && name.trim().length > 0;

	const buildPayload = (
		patch?: Partial<RollUpsertPayload>,
	): RollUpsertPayload => ({
		Id: roll?.Id && roll.Id > 0 ? roll.Id : null,
		Name: name.trim(),
		Description: description.trim() || null,
		RollApplicationId: applicationId,
		BlankDiameter: parseFloatOrNull(blankDiameter),
		Diameter: parseFloatOrNull(diameter),
		Hardness: parseIntOrNull(hardness),
		RollMaterialId: materialId,
		MillSizeId: millSizeId,
		Cost: parseFloatOrNull(cost),
		CostCenterId: costCenterId,
		OrderTimestamp: toDateString(dates.OrderTimestamp),
		ReceiveTimestamp: toDateString(dates.ReceiveTimestamp),
		InvoiceTimestamp: toDateString(dates.InvoiceTimestamp),
		PurchaseOrderItemId: roll?.PurchaseOrderItemId ?? null,
		SupplierId: supplierId,
		SupplierInvoiceNumber: supplierInvoiceNumber.trim() || null,
		Returned: toDateString(dates.Returned),
		...patch,
	});

	const savePatch = (patch: Partial<RollUpsertPayload>) => {
		if (!roll) return;
		onSave({ ...EMPTY_PATCH, Id: roll.Id, Name: name.trim(), ...patch });
	};

	const autosaveText = (
		field: keyof RollUpsertPayload,
		newValue: string | number | null,
	) => {
		if (isReadOnly || isNew || !roll) return;
		if (nameError) return;
		const original = (roll as unknown as Record<string, unknown>)[
			field as string
		];
		const normOrig = original == null || original === "" ? null : original;
		const normNew = newValue == null || newValue === "" ? null : newValue;
		if (normOrig === normNew) return;
		savePatch({ [field]: newValue });
	};

	const autosaveSelect = (
		field: keyof RollUpsertPayload,
		newValue: number | string | null,
	) => {
		if (isReadOnly || isNew || !roll) return;
		if (nameError) return;
		const original = (roll as unknown as Record<string, unknown>)[
			field as string
		];
		const normOrig = original == null ? null : original;
		const normNew = newValue == null ? null : newValue;
		if (normOrig === normNew) return;
		savePatch({ [field]: newValue });
	};

	// Saved on blur or on a calendar pick, never while typing: typing the year
	// goes through 0002, 0020, 0202... The procedure cannot clear a date (same
	// as legacy), so on an existing roll an empty or invalid input goes back to
	// the stored date. The stored time of day is kept.
	const commitDate = (field: DateField, value: Moment | null) => {
		if (!roll) return;
		const raw = roll[field];
		const stored = toDateValue(raw);
		if (!value?.isValid() || value.isBefore(MIN_DATE)) {
			if (value != null || !isNew) {
				setDates((d) => ({ ...d, [field]: stored }));
			}
			return;
		}
		if (isReadOnly || isNew || nameError) return;
		const picked = value.format(DATE_FORMAT);
		if (picked === toDateString(stored)) return;
		if (picked === lastSentDate.current[field]) return;
		lastSentDate.current[field] = picked;
		const time = raw ? String(raw).slice(10) : "";
		savePatch({ [field]: picked + time });
	};

	const renderDate = (field: DateField, label: string) => (
		<DatePicker
			label={label}
			format="MM/DD/YYYY"
			value={dates[field]}
			minDate={MIN_DATE}
			onChange={(v) => setDates((d) => ({ ...d, [field]: v }))}
			onAccept={(v) => commitDate(field, v)}
			disabled={isReadOnly}
			slotProps={{
				textField: {
					size: "small",
					fullWidth: true,
					onBlur: () => commitDate(field, dates[field]),
					InputLabelProps: { shrink: true },
					sx: EDITABLE_FIELD_SX,
				},
			}}
		/>
	);

	const renderSelect = (
		label: string,
		value: number | null,
		setValue: (v: number | null) => void,
		field: keyof RollUpsertPayload,
		options: { Id: number; Name: string }[] | undefined,
	) => (
		<TextField
			select
			label={label}
			value={value == null ? "" : String(value)}
			onChange={(e) => {
				const v = e.target.value === "" ? null : Number(e.target.value);
				setValue(v);
				autosaveSelect(field, v);
			}}
			fullWidth
			size="small"
			disabled={isReadOnly}
			slotProps={{ inputLabel: { shrink: true } }}
			sx={EDITABLE_FIELD_SX}
		>
			<MenuItem value="">-</MenuItem>
			{(options ?? []).map((m) => (
				<MenuItem key={m.Id} value={String(m.Id)}>
					{m.Name}
				</MenuItem>
			))}
		</TextField>
	);

	const renderNumber = (
		label: string,
		value: string,
		setValue: (v: string) => void,
		field: keyof RollUpsertPayload,
		integer = false,
	) => (
		<TextField
			label={label}
			type="number"
			value={value}
			onChange={(e) => setValue(e.target.value)}
			onBlur={(e) =>
				autosaveText(
					field,
					integer
						? parseIntOrNull(e.target.value)
						: parseFloatOrNull(e.target.value),
				)
			}
			fullWidth
			size="small"
			disabled={isReadOnly}
			slotProps={{
				inputLabel: { shrink: true },
				htmlInput: { step: integer ? 1 : "any" },
			}}
			sx={EDITABLE_FIELD_SX}
		/>
	);

	const handleCreate = () => {
		if (!canCreate || !roll) return;
		onSave(buildPayload());
	};

	if (!roll) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 3,
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">
					Select a roll to view details, or click "Add" to create a new one.
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<DetailHeader
				title={isNew ? "New Roll" : (roll.Name ?? "")}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNew && (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					)
				}
			/>
			<DetailBody>
				{/* When `disabled`, inputs are greyed out and the reason follows the
				    cursor as a tooltip instead of a banner that bloats the panel. */}
				<Tooltip
					title={disabled ? disabledReason : ""}
					followCursor
					disableHoverListener={!disabled}
				>
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							overflow: "auto",
							display: "flex",
							flexDirection: "column",
							gap: 0.75,
						}}
					>
						<DetailSection title="Identification" icon={<InfoOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Name"
										value={name}
										onChange={(e) => setName(e.target.value)}
										onBlur={(e) => autosaveText("Name", e.target.value.trim())}
										fullWidth
										size="small"
										required
										inputProps={{ maxLength: 12 }}
										disabled={isReadOnly}
										error={!!nameError}
										helperText={nameError}
										slotProps={{ inputLabel: { shrink: true } }}
										sx={EDITABLE_FIELD_SX}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Description"
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										onBlur={(e) =>
											autosaveText("Description", e.target.value.trim() || null)
										}
										fullWidth
										size="small"
										inputProps={{ maxLength: 200 }}
										disabled={isReadOnly}
										slotProps={{ inputLabel: { shrink: true } }}
										sx={EDITABLE_FIELD_SX}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									{renderSelect(
										"Mill Size",
										millSizeId,
										setMillSizeId,
										"MillSizeId",
										millSizesQuery.data,
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									{renderSelect(
										"Roll Application",
										applicationId,
										setApplicationId,
										"RollApplicationId",
										applicationsQuery.data,
									)}
								</Grid2>
							</Grid2>
						</DetailSection>

						<DetailSection title="Specs" icon={<StraightenOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{/* A turned roll keeps its diameter, as the legacy `wasTurned` guard did. */}
									{!isNew && roll.WasTurned ? (
										<ReadOnlyField
											label="Diameter (already turned)"
											value={diameter}
											numeric
										/>
									) : (
										renderNumber("Diameter", diameter, setDiameter, "Diameter")
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderNumber(
										"Blank Diameter",
										blankDiameter,
										setBlankDiameter,
										"BlankDiameter",
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderNumber(
										"Hardness",
										hardness,
										setHardness,
										"Hardness",
										true,
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderSelect(
										"Roll Material",
										materialId,
										setMaterialId,
										"RollMaterialId",
										materialsQuery.data,
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderNumber("Cost", cost, setCost, "Cost")}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderSelect(
										"G/L Cost Center",
										costCenterId,
										setCostCenterId,
										"CostCenterId",
										costCentersQuery.data,
									)}
								</Grid2>
							</Grid2>
						</DetailSection>

						<DetailSection
							title="Order & Supplier"
							icon={<ReceiptLongOutlinedIcon />}
						>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderDate("OrderTimestamp", "Order Date")}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderDate("ReceiveTimestamp", "Receive Date")}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									{renderDate("InvoiceTimestamp", "Invoice Date")}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 8 }}>
									{renderSelect(
										"Supplier",
										supplierId,
										setSupplierId,
										"SupplierId",
										suppliersQuery.data,
									)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 4 }}>
									<TextField
										label="Supplier Invoice No."
										value={supplierInvoiceNumber}
										onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
										onBlur={(e) =>
											autosaveText(
												"SupplierInvoiceNumber",
												e.target.value.trim() || null,
											)
										}
										fullWidth
										size="small"
										inputProps={{ maxLength: 50 }}
										disabled={isReadOnly}
										slotProps={{ inputLabel: { shrink: true } }}
										sx={EDITABLE_FIELD_SX}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>

						{!isNew && (
							<DetailSection title="Status" icon={<FlagOutlinedIcon />}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<ReadOnlyField
											label="Status"
											value={roll.StatusName ?? roll.StatusCode}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										<ReadOnlyField
											label="Order Status"
											value={roll.OrderStatusName}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										{renderDate("Returned", "Returned")}
									</Grid2>
									<Grid2 size={{ xs: 12, md: 3 }}>
										{renderNumber(
											"Scrap Weight",
											scrapWeight,
											setScrapWeight,
											"ScrapWeight",
											true,
										)}
									</Grid2>
								</Grid2>
							</DetailSection>
						)}
					</Box>
				</Tooltip>
			</DetailBody>
		</Box>
	);
};
